import { injectable } from "inversify";
import { Result, ok, err, combine } from "neverthrow";
import {
  ITurnLite,
  TurnLite,
  ILineSet,
  IEarlyDriveData,
} from "@/domain/entities/lineInfo";
import { IBusStopLite, BusStopLite } from "@/domain/entities/busStop";
import { ParseError } from "../../shared/errors/parseError";
import {
  HttpTurnLite,
  ITurnLiteParser,
  HttpLineSet,
  HttpBusStopLite,
  HttpEarlyDriveInfo,
} from "../lineInfoRepository.types";
import { WeekdayCode, WeekdayCodeName } from "@/types";

function parseLineSet(lineSet: HttpLineSet): ILineSet {
  return {
    lineSetCodeUid: lineSet.lineSetCodeuid,
    name: lineSet.name,
    displayText: lineSet.shortName ? lineSet.shortName : lineSet.name,
    isToday: lineSet.todayYn === "Y",
  };
}

function parseBusStopLite(
  data: HttpBusStopLite
): Result<IBusStopLite, ParseError> {
  try {
    const busStopLite = new BusStopLite({
      id: data.uid,
      busStopName: data.name,
      gateNoList: data.gateNoList,
    });

    return ok(busStopLite);
  } catch (error: any) {
    return err(new ParseError(error.message));
  }
}

function convertWeekdayName(weekdayCode: string): WeekdayCodeName {
  const weekdayCodeMap: Record<string, WeekdayCodeName> = {
    [WeekdayCode.MON]: WeekdayCodeName.MON,
    [WeekdayCode.TUE]: WeekdayCodeName.TUE,
    [WeekdayCode.WED]: WeekdayCodeName.WED,
    [WeekdayCode.THU]: WeekdayCodeName.THU,
    [WeekdayCode.FRI]: WeekdayCodeName.FRI,
    [WeekdayCode.SAT]: WeekdayCodeName.SAT,
    [WeekdayCode.SUN]: WeekdayCodeName.SUN,
    [WeekdayCode.HOLI]: WeekdayCodeName.HOLI,
  };

  return weekdayCodeMap[weekdayCode] ?? WeekdayCodeName.NONE;
}

function sortByWeekday(earlyDriveInfos: IEarlyDriveData[]): IEarlyDriveData[] {
  // 요일에 따라 정렬(월,화,수,목,금,토,일,휴 순)
  const sortedWeekDay = Object.values(WeekdayCode);

  const sortedEarlyDriveInfos = earlyDriveInfos?.sort((a, b) => {
    const indexOfA = sortedWeekDay.findIndex(
      (weekday) => weekday === a.weekdayCode
    );
    const indexOfB = sortedWeekDay.findIndex(
      (weekday) => weekday === b.weekdayCode
    );
    return indexOfA - indexOfB;
  });

  return sortedEarlyDriveInfos;
}

function getEarlyDriveInfos(
  earlyDriveInfos: HttpEarlyDriveInfo[] | undefined
): IEarlyDriveData[] | undefined {
  if (!earlyDriveInfos) return;

  const parsedEarlyDriveInfos = earlyDriveInfos?.map((earlyDriveInfo) => {
    return {
      weekdayCode: earlyDriveInfo.weekdayCode,
      weekdayName: convertWeekdayName(earlyDriveInfo.weekdayCode),
      earlyMinute: earlyDriveInfo.earlyMinute,
    };
  });

  const sortedEarlyDriveInfos = sortByWeekday(parsedEarlyDriveInfos);

  return sortedEarlyDriveInfos;
}

@injectable()
export class TurnLiteParser implements ITurnLiteParser {
  toDomain(data: HttpTurnLite): Result<ITurnLite, ParseError> {
    try {
      const stationsResult = combine(
        data.stations.map((station) => parseBusStopLite(station))
      );

      if (stationsResult.isErr()) {
        return err(stationsResult.error);
      }

      const earlyDriveInfos = getEarlyDriveInfos(data.earlyDriveWeekly);

      const turnLite = new TurnLite({
        id: data.uid,
        turnNum: data.no.toString(),
        turnName: data.name,
        earlyDriveInfos,
        beginStationDepartureTime: data.beginStationDepartureTime,
        stations: stationsResult.value,
        bookYn: data.bookYn,
        lineSets: data.lineSets?.map(parseLineSet) ?? [],
      });

      return ok(turnLite);
    } catch (error: any) {
      return err(new ParseError(error.message));
    }
  }
}
